export interface EarningsStatDataPoint {
  label: string | number;
  currency?: string;
  expected: number | null;
  reported: number | null;
}

export interface EarningsStatDataSet {
  yearly: EarningsStatDataPoint[];
  quarterly: EarningsStatDataPoint[];
}

export interface EarningsStats {
  revenue_data: EarningsStatDataSet;
  profit_data: EarningsStatDataSet;
  eps_data?: EarningsStatDataSet;
}

// Hardcoded until the backend provides this.
export const NEXT_EARNINGS_DATE = '14.04.2026';

export function deriveEpsData(
  content: Record<string, unknown> | null | undefined,
): EarningsStatDataSet | null {
  const stats = content?.['earnings_stats'] as Partial<EarningsStats> | undefined;
  const eps = stats?.eps_data;
  if (!eps || !Array.isArray(eps.quarterly)) return null;
  const populatedQuarters = eps.quarterly.filter((p) => p.expected !== null || p.reported !== null);
  return populatedQuarters.length >= 3 ? eps : null;
}

export function deriveRevenueStats(
  content: Record<string, unknown> | null | undefined,
): EarningsStats | null {
  const stats = content?.['earnings_stats'] as Partial<EarningsStats> | undefined;
  if (!stats?.revenue_data || !stats?.profit_data) return null;
  return stats as EarningsStats;
}

// e.g. "Q1 26" — the bare fiscal period, reused by formatReportPeriod below and by the desktop
// "Zum letzten Quartal" subtitle (earningsAnalysisData's fiscalPeriod).
export function formatFiscalPeriod(content: Record<string, unknown> | null | undefined): string {
  const rp = content?.['report_period'] as
    | { fiscal_year?: number; fiscal_period?: string }
    | undefined;
  if (!rp?.fiscal_period || rp.fiscal_year == null) return '';
  const shortYear = String(rp.fiscal_year % 100).padStart(2, '0');
  return `${rp.fiscal_period} ${shortYear}`;
}

export function formatReportPeriod(content: Record<string, unknown> | null | undefined): string {
  const period = formatFiscalPeriod(content);
  return period ? `Earnings-Ergebnis ${period}` : '';
}

// Shared with earningsAnalysisData() so the symbol shown next to a chart always matches the one
// EpsChart itself derives from the same data.
export function epsCurrencySymbol(data: EarningsStatDataSet | null): string {
  const currency = data?.quarterly.find((p) => p.currency)?.currency ?? 'USD';
  if (currency === 'EUR') return '€';
  if (currency === 'USD') return '$';
  return currency;
}

export interface QuarterSurprise {
  percent: number;
  beat: boolean;
}

// Skips a trailing upcoming quarter (reported === null). Null when no quarter has both fields.
export function deriveQuarterSurprise(data: EarningsStatDataSet | null): QuarterSurprise | null {
  const quarterly = data?.quarterly ?? [];
  for (let i = quarterly.length - 1; i >= 0; i--) {
    const q = quarterly[i];
    if (q.reported !== null && q.expected !== null) {
      if (q.expected === 0) return null;
      const percent = Math.round(((q.reported - q.expected) / Math.abs(q.expected)) * 100);
      return { percent, beat: percent >= 0 };
    }
  }
  return null;
}

// Dative ordinal, e.g. "Bericht zum zweiten Quartal".
const QUARTER_ORDINALS: Record<number, string> = {
  1: 'ersten',
  2: 'zweiten',
  3: 'dritten',
  4: 'vierten',
};

// Derived from the trailing not-yet-reported quarter, e.g. "Bericht zum ersten Quartal".
export function deriveNextEarningsReportLabel(data: EarningsStatDataSet | null): string {
  const upcoming = data?.quarterly.find((q) => q.reported === null);
  if (!upcoming) return 'Nächster Bericht';
  const quarterNumber = String(upcoming.label).match(/^Q(\d)/)?.[1];
  const ordinal = quarterNumber ? QUARTER_ORDINALS[Number(quarterNumber)] : undefined;
  return ordinal ? `Bericht zum ${ordinal} Quartal` : 'Nächster Bericht';
}
